import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Eye,
    Package,
    QrCode,
    ShieldCheck,
    RefreshCw,
    Building2,
    ClipboardList,
} from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import { FloatingContact } from "../components/visuals/FloatingContact";
import { ScrollProgress } from "../components/visuals/ScrollProgress";
import useReveal from "../lib/useReveal";

const features = [
    { icon: Package, label: "Detection Kit Dispensers" },
    { icon: Eye, label: "Easy Access for Guests & Staff" },
    { icon: QrCode, label: "QR-Accessible Instructions" },
    { icon: Building2, label: "Venue Implementation" },
    { icon: ClipboardList, label: "Kit Organization" },
    { icon: RefreshCw, label: "Restocking Options" },
    { icon: ShieldCheck, label: "Multi-Location Programs" },
];

const flow = [
    {
        step: "Access",
        desc: "Detection kits are available through a clearly marked Kairos dispenser or designated safety station.",
    },
    {
        step: "Test",
        desc: "Users follow the detection kit manufacturer's instructions to screen the beverage for the substances that specific kit is designed to detect.",
    },
    {
        step: "Respond",
        desc: "If tampering is suspected, the individual or venue should follow appropriate safety and emergency procedures.",
    },
];

export default function DrinkSpikeDetectionPage() {
    useReveal();
    return (
        <main data-testid="drink-spike-detection-page" className="min-h-screen bg-white">
            <ScrollProgress />
            <Navbar />

            {/* Hero */}
            <section className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-32 -right-24 w-[520px] h-[520px] opacity-[0.08] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>
                <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] opacity-[0.05] pointer-events-none float-slower">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 100,40 180,160" fill="#3B82F6" />
                    </svg>
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <Link
                        to="/"
                        data-testid="spike-back"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Back to Kairos
                    </Link>

                    <div className="inline-flex items-center gap-3 mb-6">
                        <span className="relative inline-flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#F0493E] pulse-ring" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F0493E]" />
                        </span>
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Public Safety · Drink Spike Detection
                        </span>
                    </div>

                    <h1 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-tighter text-balance max-w-5xl">
                        Detection kits,
                        <br />
                        visible and accessible.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed font-medium">
                        Kairos helps venues and organizations make drink-spike
                        detection resources visible, organized, and accessible —
                        so guests and staff know exactly where to find them.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/contact"
                            data-testid="spike-cta-contact"
                            className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                        >
                            Bring spike detection to your venue
                            <ArrowRight size={18} strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features grid — mirrors integrations grid on Alert & Automation */}
            <section className="relative bg-white py-24 md:py-32 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="reveal max-w-3xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            What Kairos Provides
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Everything a venue
                            <br />
                            needs to make kits easy to reach.
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                        {features.map((f, idx) => (
                            <div
                                key={f.label}
                                data-testid={`spike-feature-${idx}`}
                                className="reveal group bg-white p-6 md:p-8 flex flex-col items-start hover:bg-slate-50 transition-colors"
                            >
                                <div className="h-11 w-11 grid place-items-center border border-[#0A1428] group-hover:border-[#F0493E] transition-colors">
                                    <f.icon size={18} strokeWidth={1.5} className="text-[#F0493E]" />
                                </div>
                                <div className="mt-5 font-display font-bold text-base md:text-lg text-[#0A1428] tracking-tight leading-snug">
                                    {f.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ACCESS → TEST → RESPOND */}
            <section className="relative bg-slate-50 py-24 md:py-32 border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="reveal max-w-3xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            How It Works
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Access &nbsp;→&nbsp; Test &nbsp;→&nbsp; Respond.
                        </h2>
                    </div>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {flow.map((s, i) => (
                            <div
                                key={s.step}
                                data-testid={`spike-flow-${i}`}
                                className="reveal bg-white border border-slate-200 p-8 md:p-10 hover:border-[#F0493E] transition-colors"
                            >
                                <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                                    Step 0{i + 1}
                                </div>
                                <h3 className="mt-6 font-display font-extrabold text-3xl md:text-4xl text-[#0A1428] tracking-tighter uppercase">
                                    {s.step}
                                </h3>
                                <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed font-medium">
                                    {s.desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Safety Disclaimer */}
                    <div
                        data-testid="spike-disclaimer"
                        className="reveal mt-14 max-w-4xl bg-white border border-slate-300 p-6 md:p-8 flex items-start gap-4"
                    >
                        <div className="shrink-0 h-8 w-8 grid place-items-center border border-slate-400 text-slate-500 text-sm font-bold">
                            !
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-slate-500 mb-2">
                                Important Safety Note
                            </div>
                            <p className="text-sm md:text-[15px] leading-relaxed text-slate-700 font-medium">
                                A screening kit is not a guarantee that a
                                beverage is safe. Detection capabilities and
                                limitations vary by product. A negative result
                                does not rule out all substances or drink
                                tampering.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative bg-[#0A1428] text-white py-20 md:py-28 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-[420px] h-[420px] opacity-[0.08] pointer-events-none">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <div>
                        <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                            Talk to Kairos
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-balance max-w-2xl">
                            Bring Spike Detection
                            <br />
                            to your venue.
                        </h3>
                    </div>
                    <Link
                        to="/contact"
                        data-testid="spike-cta-bottom"
                        className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm self-start md:self-end"
                    >
                        Contact Us
                        <ArrowRight size={18} strokeWidth={2} />
                    </Link>
                </div>
            </section>

            <Footer />
            <FloatingContact />
        </main>
    );
}
